import type { ComponentPropsWithRef, ElementType, JSX, ReactNode } from 'react';
import type { FieldValues, Path } from 'react-hook-form';

import { cn } from '@pesto/utils';

import { Checkbox } from '../../components/Checkbox';
import { genericForwardRef } from '../../utils/composition';

type FormCheckboxBaseProps<TFieldValues extends FieldValues> = {
  label?: ReactNode;
  helperText?: ReactNode;
  error?: string;
  description?: ReactNode;
  required?: boolean;
  wrapperClassName?: string;
  labelClassName?: string;
  checkboxClassName?: string;
  onChange?: (checked: boolean) => void;
  id?: string;
  name: Path<TFieldValues>;
  readOnly?: boolean;
  disabled?: boolean;
};

export type FormCheckboxProps<
  TFieldValues extends FieldValues = FieldValues,
  T extends ElementType = typeof Checkbox,
> = FormCheckboxBaseProps<TFieldValues> & {
  as?: T;
} & Omit<ComponentPropsWithRef<T>, keyof FormCheckboxBaseProps<TFieldValues>>;

const FormCheckboxInner = <TFieldValues extends FieldValues = FieldValues, T extends ElementType = typeof Checkbox>(
  {
    label,
    helperText,
    error,
    description,
    required,
    wrapperClassName,
    labelClassName,
    checkboxClassName,
    id: providedId,
    as,
    className,
    name,
    onChange,
    'aria-describedby': ariaDescribedBy,
    readOnly,
    disabled,
    ...props
  }: FormCheckboxProps<TFieldValues, T>,
  ref: ComponentPropsWithRef<T>['ref'],
) => {
  const id = providedId || `form-field-${name}`;
  const helperId = `${id}-helper`;
  const errorId = `${id}-error`;
  const descriptionId = `${id}-description`;

  const describedBy = [ariaDescribedBy, helperText && helperId, error && errorId, description && descriptionId]
    .filter(Boolean)
    .join(' ');

  const Component = as || Checkbox;

  const handleOnChange = (checked: boolean) => {
    onChange?.(checked);
  };

  return (
    <div className={cn('space-y-2', wrapperClassName)}>
      <div className="flex items-start space-x-2">
        <Component
          ref={ref}
          id={id}
          name={name}
          onCheckedChange={handleOnChange}
          aria-describedby={describedBy || undefined}
          aria-invalid={!!error}
          aria-required={required}
          className={cn(error && 'border-destructive', checkboxClassName, className)}
          disabled={readOnly || disabled}
          {...props}
        />
        <div className="flex-1 space-y-1">
          {label && (
            <div className="flex justify-between">
              <label
                htmlFor={id}
                className={cn(
                  'text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
                  error && 'text-destructive',
                  labelClassName,
                )}
              >
                {label}
                {required && !readOnly && <span className="text-destructive ml-1">*</span>}
              </label>
              {description && (
                <span id={descriptionId} className="text-muted-foreground text-xs">
                  {description}
                </span>
              )}
            </div>
          )}
          {(helperText || error) && (
            <div className="space-y-1">
              {helperText && (
                <p id={helperId} className="text-muted-foreground text-sm">
                  {helperText}
                </p>
              )}
              {error && (
                <p id={errorId} className="text-destructive text-sm">
                  {error}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// SAFETY: forwardRef only attaches the ref; props reach FormCheckboxInner unchanged, so its generic signature holds.
export const FormCheckbox = genericForwardRef(FormCheckboxInner, 'FormCheckbox') as <
  TFieldValues extends FieldValues = FieldValues,
  T extends ElementType = typeof Checkbox,
>(
  props: FormCheckboxProps<TFieldValues, T> & { ref?: ComponentPropsWithRef<T>['ref'] },
) => JSX.Element;
